import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Voice search (Section 11 step 5) — Web Speech API, Chromium-first.
 *
 * The recognition runs entirely in the browser: no mic audio ever leaves the
 * device. Browsers without SpeechRecognition (Firefox, Safari desktop) simply
 * don't expose the button (`supported` is false) — graceful degradation.
 *
 * Commands are free-form; `parseVoiceCommand` strips intent verbs and content
 * type words so "play ESPN" searches "espn" and "find action movies" filters
 * to Movies with query "action".
 */

// Minimal local types — SpeechRecognition is not part of standard lib.dom.
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string; message?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function recognitionCtor(): SpeechRecognitionCtor | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as unknown as { SpeechRecognition?: SpeechRecognitionCtor; webkitSpeechRecognition?: SpeechRecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

/** Content-type filter a voice phrase maps to, or undefined to leave the
 * current filter alone. */
export type VoiceFilter = "movie" | "series" | "channel" | "program";

/** Intent verbs that add nothing to the search, e.g. "play", "find", "show me". */
const INTENT_VERB = /^(?:play|watch|find|search|show me|show|open|look up|let'?s (?:play|watch|find|see|search))\s+/i;
/** Trailing content-type word ("ESPN channel" → "espn", filter channel). */
const TAIL_TYPE =
  /(\s+)(?:movie|movies|film|films|series|show|shows|channel|channels|guide|program|programs)$/i;

/**
 * Turn a voice utterance into a search query + optional filter.
 * "play ESPN" → { query: "espn" }
 * "find action movies" → { query: "action", filter: "movie" }
 * "the big bang theory" → { query: "the big bang theory" } (no verbs, untouched)
 */
export function parseVoiceCommand(raw: string): { query: string; filter?: VoiceFilter } {
  const text = raw.trim();
  if (!text) return { query: "" };

  let query = text.replace(INTENT_VERB, "").trim() || text;
  let filter: VoiceFilter | undefined;

  // Type words only count as the LAST token, so real titles like "The Tonight
  // Show" keep their words; only "play espn channel" / "find action movies"
  // lose the type word. The downstream search is substring-based, so a short
  // remainder ("action") still finds its titles.
  const tail = query.match(TAIL_TYPE);
  if (tail) {
    const word = tail[0].trim().toLowerCase();
    if (word === "movie" || word === "movies" || word === "film" || word === "films") filter = "movie";
    else if (word === "series" || word === "show" || word === "shows") filter = "series";
    else if (word === "channel" || word === "channels") filter = "channel";
    else filter = "program"; // guide / program(s)
    query = query.slice(0, tail.index).trim();
  }

  return filter ? { query, filter } : { query };
}

export interface VoiceSearch {
  /** True when this browser has the Web Speech API — the mic button is only
   *  shown then (Firefox/Safari desktop degrade to no button at all). */
  supported: boolean;
  listening: boolean;
  /** Human-readable error to show under the bar; null when idle. Auto-clears. */
  error: string | null;
  start: () => void;
  stop: () => void;
}

export function useVoiceSearch(onTranscript: (text: string) => void): VoiceSearch {
  const [supported] = useState(() => recognitionCtor() !== undefined);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const cbRef = useRef(onTranscript);
  cbRef.current = onTranscript;
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearErrorLater = useCallback(() => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => setError(null), 5000);
  }, []);

  const stop = useCallback(() => {
    recRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    const Ctor = recognitionCtor();
    if (!Ctor) return;
    setError(null);
    try {
      const rec = new Ctor();
      recRef.current = rec;
      rec.lang = navigator.language || "en-US";
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.continuous = false;

      rec.onresult = (event) => {
        const last = event.results[event.results.length - 1];
        const transcript = last?.[0]?.transcript?.trim();
        if (last?.isFinal && transcript) cbRef.current(transcript);
      };
      rec.onerror = (event) => {
        if (event.error === "aborted") return;
        if (event.error === "not-allowed" || event.error === "service-not-allowed") {
          setError("Microphone permission denied — allow it in your browser settings.");
        } else if (event.error === "no-speech") {
          setError("No speech detected — try again.");
        } else if (event.error === "network") {
          setError("Speech service unreachable — check your connection and try again.");
        } else {
          setError("Voice search didn't work — try again.");
        }
        clearErrorLater();
      };
      rec.onend = () => {
        setListening(false);
        recRef.current = null;
      };

      rec.start();
      setListening(true);
    } catch {
      setError("Voice search isn't available right now.");
      setListening(false);
      clearErrorLater();
    }
  }, [clearErrorLater]);

  // Clean up a live session (and its timers) on unmount.
  useEffect(
    () => () => {
      recRef.current?.abort();
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    },
    [],
  );

  return { supported, listening, error, start, stop };
}
